/**
 * CategoryDialog Component
 * 
 * Dialog for creating and editing categories with form fields for
 * name and group selection.
 */

import React, { ReactElement } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Alert,
  FormHelperText,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";
import CannotChangeLaterIcon from "../../../components/CannotChangeLaterIcon";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

interface CategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedTab: "expense" | "income";
  editingCategory: TransactionCategory | null;
  groups: TransactionCategoryGroup[];
  categoryName: string;
  setCategoryName: (name: string) => void;
  categoryGroupId: string;
  setCategoryGroupId: (id: string) => void;
  groupInputValue: string;
  setGroupInputValue: (value: string) => void;
  categoryNameError: string;
  categoryNameWarning: string;
  error: string;
  saving: boolean;
  checkCategoryDuplicate: (name: string, groupId: string) => void;
}

/**
 * Dialog for adding or editing a category
 */
function CategoryDialog({
  open,
  onClose,
  onConfirm,
  selectedTab,
  editingCategory,
  groups,
  categoryName,
  setCategoryName,
  categoryGroupId,
  setCategoryGroupId,
  groupInputValue,
  setGroupInputValue,
  categoryNameError,
  categoryNameWarning,
  error,
  saving,
  checkCategoryDuplicate,
}: CategoryDialogProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isEditMode = !!editingCategory;

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Category" : `Add ${selectedTab === "expense" ? "Expense" : "Income"} Category`}
      confirmText={saving ? (isEditMode ? "Saving..." : "Adding...") : (isEditMode ? "Save" : "Add")}
      onConfirm={onConfirm}
      confirmDisabled={saving || !categoryName.trim() || !categoryGroupId || !!categoryNameError}
      maxWidth="sm"
      positionAtTopOnMobile
    >
      <Box>
        <TextField
          autoFocus
          margin="dense"
          label="Category Name"
          fullWidth
          variant="outlined"
          value={categoryName}
          onChange={(e) => {
            const value = e.target.value;
            setCategoryName(value);
            if (!isEditMode) {
              checkCategoryDuplicate(value, categoryGroupId);
            }
          }}
          disabled={saving}
          required
          error={!!categoryNameError}
          helperText={categoryNameError || undefined}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !saving && categoryName.trim() && categoryGroupId && !categoryNameError) {
              onConfirm();
            }
          }}
        />
        {categoryNameWarning && (
          <FormHelperText sx={{ color: "warning.main", mt: 0.5, mx: 1.75 }}>
            {categoryNameWarning}
          </FormHelperText>
        )}
      </Box>
      
      <Box sx={{ width: "100%" }}>
        <Autocomplete
          freeSolo
          options={groups}
          value={categoryGroupId ? groups.find((g) => g.id === categoryGroupId) || null : null}
          inputValue={groupInputValue}
          onInputChange={(_, newInputValue) => {
            if (isEditMode) {
              // Disabled in edit mode - don't allow changes
              return;
            }
            setGroupInputValue(newInputValue);
            // Check if input matches an existing group
            const matchingGroup = groups.find(
              (g) => g.name.toLowerCase() === newInputValue.trim().toLowerCase()
            );
            const newGroupId = matchingGroup ? matchingGroup.id : "";
            setCategoryGroupId(newGroupId);
            // Check for duplicates when group changes
            if (categoryName) {
              checkCategoryDuplicate(categoryName, newGroupId);
            }
          }}
          onChange={(_, newValue) => {
            if (isEditMode) {
              // Disabled in edit mode - don't allow changes
              return;
            }
            const newGroupId = newValue && typeof newValue !== "string" ? newValue.id : "";
            if (newValue && typeof newValue !== "string") {
              setCategoryGroupId(newGroupId);
              setGroupInputValue(newValue.name);
            } else if (!newValue) {
              setCategoryGroupId("");
              setGroupInputValue("");
            }
            // Check for duplicates when group changes
            if (categoryName) {
              checkCategoryDuplicate(categoryName, newGroupId);
            }
          }}
          filterOptions={(options, params) => {
            const { inputValue: filterInputValue } = params;
            
            // When input is empty, show all groups (no filtering, no Create option)
            if (!filterInputValue.trim()) {
              return options;
            }
            
            // When input has text, filter options (no Create option in Add Category dialog)
            const filtered = options.filter((option) =>
              option.name.toLowerCase().includes(filterInputValue.toLowerCase())
            );
            
            return filtered;
          }}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return option.name;
          }}
          renderInput={(params) => {
            // Show preview of groups when there are groups and input is empty (only in add mode)
            const previewText = !isEditMode && groups.length > 0 && !groupInputValue
              ? (groups.length <= 3 
                  ? groups.map(g => g.name).join(" / ")
                  : groups.slice(0, 3).map(g => g.name).join(" / ") + "...")
              : "";
            return (
              <TextField
                {...params}
                label="Group"
                margin="dense"
                required
                error={!categoryGroupId && !!error}
                helperText={
                  isEditMode 
                    ? "Group cannot be changed after creation"
                    : (!categoryGroupId && error ? error : undefined)
                }
                placeholder={
                  isEditMode 
                    ? "Group cannot be changed after creation"
                    : (!groupInputValue && groups.length > 0 ? previewText : "Type to select a group")
                }
                disabled={saving || isEditMode}
                inputProps={{
                  ...params.inputProps,
                  // On mobile: tap opens dropdown only, no keyboard
                  readOnly: isMobile,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!isEditMode && (
                        <InputAdornment position="end" sx={{ ml: 0 }}>
                          <CannotChangeLaterIcon
                            tooltipMessage="Group cannot be changed after category creation"
                            position="inline"
                          />
                        </InputAdornment>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }}
          renderOption={(props, option) => {
            if (typeof option === "string") {
              return <li {...props} key={option}>{option}</li>;
            }
            
            // Simple display without delete button in Add Category dialog
            return (
              <li {...props} key={option.id}>
                <Typography sx={{ flex: 1 }}>{option.name}</Typography>
              </li>
            );
          }}
          disabled={saving || isEditMode}
        />
      </Box>
      
      {error && (
        <Alert severity="error">{error}</Alert>
      )}
    </StandardDialog>
  );
}

export default CategoryDialog;
